import {REG} from "/@lib@/commons/registry.js";
import {Action, ActionMenu, ActionWrapperFromSvc, IAction} from "/@lib@/commons/actions.js";
import {doBoxPaste, IEltBoxSelection, isEltBoxSelection, isWedletBoxSelection, IWedEditorBoxSel, IWedletBoxSelection} from "/@back@/edit/wed/features/boxSel.js";
import {WedEditAction} from "/@back@/edit/wed/wedEditor.js";
import {EWedletEditMode, IElementWedlet, IS_EltWedlet, isCustomClipboard, IWedletActionCtx, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {OWedEditorBoxConfig} from "/@back@/edit/wed/wedEditorBox.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {JML} from "/@lib@/commons/xml/jml.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";


export class BoxEditAction<C extends IWedletActionCtx> extends WedEditAction<C> {
	isEnabled(ctx: C): boolean {
		if (!WEDLET.isWritableWedlet(ctx.wedlet)) return false;
		return super.isEnabled(ctx);
	}
}


//####### Deletes

REG.reg.registerSvc('wedBoxSelExclude', 1, new Action<IWedEditorBoxSel | IWedletActionCtx>('wedBoxSelExclude')
	.setLabel("Exclure la sélection")
	.setIcon("/@skin@/edit/wed/box/excludeTag.svg")
	.setVisible((ctx: IWedEditorBoxSel | IWedletActionCtx) => {
		const wedMgr = ctx.wedMgr;
		const boxSelMgr = (wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
		return (boxSelMgr && !boxSelMgr.selInVirtual && !(wedMgr.wedEditor.config as OWedEditorBoxConfig).disableBoxSelection && !boxSelMgr.range.isCollapsed && XA.isInSameSeq(boxSelMgr.range.start, boxSelMgr.range.end));
	})
	.setEnabled((ctx) => {
		//setVisible() a déjà vérifié une sélection valide (sameSeq en particulier),
		//boxSelMgr.activeWedlet est donc nécessairement un des wedlets sélectionnés, on peut remonter au père pour valider la permission en écriture.
		return WEDLET.resolveEditMode((ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.activeWedlet?.wedParent) === EWedletEditMode.write;
	})
	.setExecute(function (this: Action<IWedEditorBoxSel | IWedletActionCtx>, ctx: IWedEditorBoxSel | IWedletActionCtx, ev: Event) {
		const wedMgr = ctx.wedMgr;
		const rg = (wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.range;
		if (!rg.isCollapsed && XA.isInSameSeq(rg.start, rg.end)) {
			const txt = wedMgr.docHolder.exportRange(rg, 'application/xml', {rootTagNs: "scenari.eu:exclude:1.0", rootTagName: "sexc:exclude"});
			const batch = wedMgr.docHolder.newBatch(rg);
			batch.deleteSequence(rg.start, XA.last(rg.end) as number - (XA.last(rg.start) as number));
			batch.insertJml(rg.start, [{'': JML.COMMENT, '=': DOM.escapeComment(txt)}]);
			batch.doBatch();
		}
	}));

REG.reg.addSvcToList("actions:wed:box", "exclude", 1, 'wedBoxSelExclude', 10);

REG.reg.registerSvc('wedBoxSelDelete', 1, new Action<IWedEditorBoxSel | IWedletActionCtx>('wedBoxSelDelete')
	.setLabel("Supprimer la sélection")
	.setIcon("/@skin@/edit/wed/deleteTag.svg")
	.setVisible((ctx: IWedEditorBoxSel | IWedletActionCtx) => {
		const wedMgr = ctx.wedMgr;
		const boxSelMgr = (wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
		return boxSelMgr && !boxSelMgr.selInVirtual && !(wedMgr.wedEditor.config as OWedEditorBoxConfig).disableBoxSelection;
	})
	.setEnabled((ctx: IWedEditorBoxSel | IWedletActionCtx) => !ctx.wedMgr.readOnly && (!(ctx as IWedletActionCtx).wedlet || WEDLET.isWritableWedlet((ctx as IWedletActionCtx).wedlet)))
	.setExecute(function (this: Action<IWedEditorBoxSel | IWedletActionCtx>, ctx: IWedEditorBoxSel | IWedletActionCtx, ev: Event) {
		const boxSelMgr = (ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
		if (boxSelMgr) boxSelMgr.deleteContentSelection();
	}));

REG.reg.addSvcToList("actions:wed:box", "delete", 1, 'wedBoxSelDelete', 20);


const delFromKeyboard = new ActionWrapperFromSvc('deleteBox')
	.setWrappedSvcCd('wedBoxSelDelete')
	.setExecute(function (this: ActionWrapperFromSvc<IWedEditorBoxSel>, ctx: IWedEditorBoxSel, ev: Event) {
		const from = ev.composedPath()[0];
		if (IS_EltWedlet(from) && isWedletBoxSelection(from.wedlet)) {
			this.getWrapped(ctx).execute(ctx, ev);
		} else return 'noPreventDefault';
	});

REG.reg.addToList("accelkeys:wed:box:sel", 'Delete', 1, delFromKeyboard);
REG.reg.addToList("accelkeys:wed:box:sel", 'Backspace', 1, delFromKeyboard);


//####### Navigation

REG.reg.addToList("accelkeys:wed:box:sel", 'Escape', 1, new Action<IWedEditorBoxSel>("boxEscape").setExecute(function (this: Action<IWedEditorBoxSel>, ctx: IWedEditorBoxSel, ev: Event) {
	const from = ev.composedPath().find((node: IEltBoxSelection, i: number) => node.selMode === 'box' && i > 0) as IElementWedlet;
	if (from) {
		(from.wedlet as IWedletBoxSelection).focusWedlet();
	} else return 'noStopPropag-noPreventDefault';
}));

REG.reg.addToList("accelkeys:wed:box:sel", 'ArrowUp', 1, new Action<IWedEditorBoxSel>("boxArrowUp").setExecute(function (this: Action<IWedEditorBoxSel>, ctx: IWedEditorBoxSel, ev: Event) {
	const from = ev.composedPath()[0];
	if (isEltBoxSelection(from)) {
		const prev = DOMSH.findFlatPrevUncle(from, ctx.rootNode, isBox);
		if (prev) {
			((prev as IEltBoxSelection).wedlet as IWedletBoxSelection).focusWedlet();
			ev.preventDefault();
		} else if (ctx.config.crossNav) return 'noStopPropag-noPreventDefault';
	} else return 'noPreventDefault';
}));

REG.reg.addToList("accelkeys:wed:box:sel", 'ArrowDown', 1, new Action<IWedEditorBoxSel>("ArrowDown").setExecute(function (this: Action<IWedEditorBoxSel>, ctx: IWedEditorBoxSel, ev: Event) {
	const from = ev.composedPath()[0];
	if (isEltBoxSelection(from)) {
		const next = DOMSH.findFlatNextUncle(from, ctx.rootNode, isBox);
		if (next) {
			((next as IEltBoxSelection).wedlet as IWedletBoxSelection).focusWedlet();
			ev.preventDefault();
		} else if (ctx.config.crossNav) return 'noStopPropag-noPreventDefault';
	} else return 'noPreventDefault';
}));


REG.reg.addToList("accelkeys:wed:box:sel", 'ArrowLeft', 1, new Action<IWedEditorBoxSel>("ArrowLeft").setExecute(function (this: Action<IWedEditorBoxSel>, ctx: IWedEditorBoxSel, ev: Event) {
	const from = ev.composedPath()[0];
	if (isEltBoxSelection(from)) {
		const prev = DOMSH.findFlatPrevious(from, ctx.rootNode, isBox);
		if (prev) {
			((prev as IEltBoxSelection).wedlet as IWedletBoxSelection).focusWedlet();
			ev.preventDefault();
		} else if (ctx.config.crossNav) return 'noStopPropag-noPreventDefault';
	} else return 'noPreventDefault';
}));

REG.reg.addToList("accelkeys:wed:box:sel", 'ArrowRight', 1, new Action<IWedEditorBoxSel>("ArrowRight").setExecute(function (this: Action<IWedEditorBoxSel>, ctx: IWedEditorBoxSel, ev: Event) {
	const from = ev.composedPath()[0];
	if (isEltBoxSelection(from)) {
		const next = DOMSH.findFlatNext(from, ctx.rootNode, isBox);
		if (next) {
			(next.wedlet as IWedletBoxSelection).focusWedlet();
			ev.preventDefault();
		} else if (ctx.config.crossNav) return 'noStopPropag-noPreventDefault';
	} else return 'noPreventDefault';
}));

function isBox(n: Node): n is IEltBoxSelection {return isEltBoxSelection(n) && n.selMode === 'box' && getComputedStyle(n).display !== 'none'}

//######## Copy / paste
function addAction(action: IAction<IWedletActionCtx>, list: string, sortKey: number, accel: string | null = null) {
	REG.reg.addToList(list, action.getId(), 1, action, sortKey);
	if (accel) REG.reg.addToList("accelkeys:wed:box", accel, 1, action);
}

addAction(new Action<IWedletActionCtx>("copy")
		.setLabel("Copier").setGroup("clipboard")
		.setVisible((ctx: IWedletActionCtx, ev) => {
			return !(ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.selInVirtual
		})
		.setExecute(async (ctx: IWedletActionCtx, ev) => {
			if (isCustomClipboard(ctx.wedlet) && await ctx.wedlet.onClipboard("copy", ev)) return;
			if (isCustomClipboard(ctx.focusedElt) && await ctx.focusedElt.onClipboard("copy", ev)) return;
			//Impl par édfaut du boxSel.
			const rg = (ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.range;
			if (XA.isAttribute(rg.start) || !rg.isCollapsed) return ctx.wedMgr.writeRangeToClipboard(rg);
		}),
	"actions:wed:box", 10, "c-accel");

addAction(new ActionMenu("copySpecial")
		.setLabel("Copier spécial").setGroup("clipboard")
		.setVisible((ctx: IWedletActionCtx, ev) => {
			return !(ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.selInVirtual
		})
		.setActionLists("wed:box:copy:special"),
	"actions:wed:box", 11);

addAction(new Action<IWedletActionCtx>("copyXml")
		.setLabel("Copier en XML")
		.setExecute((ctx: IWedletActionCtx, ev) => {
			const rg = (ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.range;
			if (XA.isAttribute(rg.start) || !rg.isCollapsed) ctx.wedMgr.writeRangeToClipboardAsXml(rg);
		}),
	"wed:box:copy:special", 10, "c-accel+shift");

addAction(new BoxEditAction("cut")
		.setLabel("Couper").setGroup("clipboard")
		.setVisible((ctx: IWedletActionCtx, ev) => {
			return !(ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.selInVirtual
		})
		.setExecute(async (ctx: IWedletActionCtx, ev) => {
			if (isCustomClipboard(ctx.wedlet) && await ctx.wedlet.onClipboard("cut", ev)) return;
			if (isCustomClipboard(ctx.focusedElt) && await ctx.focusedElt.onClipboard("cut", ev)) return;
			//Impl par édfaut du boxSel.
			const boxSelMgr = (ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
			const rg = boxSelMgr.range;
			if (XA.isAttribute(rg.start) || !rg.isCollapsed) {
				await ctx.wedMgr.writeRangeToClipboard(rg);
				boxSelMgr.deleteContentSelection();
			}
		}),
	"actions:wed:box", 20, "x-accel");

addAction(new BoxEditAction("paste")
		.setLabel("Coller").setGroup("clipboard")
		.setExecute((ctx: IWedletActionCtx, ev) => {
			async function doPaste() {
				if (isCustomClipboard(ctx.wedlet) && await ctx.wedlet.onClipboard("paste", ev)) return;
				if (isCustomClipboard(ctx.focusedElt) && await ctx.focusedElt.onClipboard("paste", ev)) return;
				//Impl par défaut du boxSel.
				doBoxPaste(ctx.wedMgr, DOMSH.findFlatParentEltOrSelf(ctx.focusedElt, ctx.wedMgr.wedEditor.rootNode, IS_EltWedlet), (ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr);
			}

			doPaste();
			return 'noPreventDefault'; //Pour gestion paste de fichiers externes via le ClipboardEvent.
		}),
	"actions:wed:box", 30, "v-accel");

// const pasteSpecial = new ActionMenu("pasteSpecial")
// 	.setLabel("Coller spécial").setGroup("clipboard")
// 	.setActionLists("wed:box:paste:special");
// REG.reg.addToList("actions:wed:box", "pasteSpecial", 1, pasteSpecial, 31);